<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { formatTime } from '@chatwoot/utils';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import getUnixTime from 'date-fns/getUnixTime';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const store = useStore();

const toFiniteNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPercent = value => {
  const num = toFiniteNumber(value);
  if (num == null) return null;
  return num <= 1 ? num * 100 : num;
};

const pickMetricNumber = (
  source,
  keys = [],
  { fallback = null, transform = toFiniteNumber } = {}
) => {
  const transformed = keys
    .map(key => transform(source?.[key]))
    .find(value => value != null);
  return transformed ?? fallback;
};

const now = endOfDay(new Date());
const since = ref(getUnixTime(startOfDay(subDays(now, 6))));
const until = ref(getUnixTime(now));
const businessHours = ref(false);
const excludeIssueTime = ref(false);
const hideInactive = ref(false);
const sortCol = ref('conversations');
const sortDesc = ref(true);
const showFilterMenu = ref(false);
const filterMenuRef = ref(null);
const showColumnsMenu = ref(false);
const columnsMenuRef = ref(null);
const showTargetModal = ref(false);
const activeTargetTab = ref('csat');
const errorMessage = ref('');
const COLUMN_STORAGE_KEY = 'wlab.agent-reports.columns.v1';
const defaultVisibleColumns = {
  satisfaction: true,
  firstResp: true,
  chattingTime: true,
  efficiency: true,
  avgResolutionTime: true,
  avgCustomerWaitingTime: true,
  resolutionCount: true,
};
const columnOptions = [
  { key: 'satisfaction', label: 'Satisfaction' },
  { key: 'firstResp', label: 'First response' },
  { key: 'chattingTime', label: 'Chatting time' },
  { key: 'efficiency', label: 'Efficiency' },
  { key: 'avgResolutionTime', label: 'Avg. Resolution Time' },
  { key: 'avgCustomerWaitingTime', label: 'Avg. Customer Waiting Time' },
  { key: 'resolutionCount', label: 'Resolution Count' },
];
const visibleColumns = ref({ ...defaultVisibleColumns });

const TARGET_STORAGE_KEY = 'wlab.agent-reports.targets.v1';
const targetTabs = [
  {
    key: 'csat',
    label: 'Chat satisfaction',
    targetDirection: 'higher',
    unit: '%',
    step: 1,
    min: 0,
    max: 100,
    recommended: 70,
  },
  {
    key: 'frt',
    label: 'First response time',
    targetDirection: 'lower',
    unit: 's',
    step: 1,
    min: 1,
    max: 86400,
    recommended: 60,
  },
  {
    key: 'efficiency',
    label: 'Efficiency',
    targetDirection: 'higher',
    unit: '',
    step: 0.1,
    min: 0.1,
    max: 999,
    recommended: 9,
  },
];

const targetMetaByKey = targetTabs.reduce((map, tab) => {
  map[tab.key] = tab;
  return map;
}, {});

const defaultTargets = targetTabs.reduce((map, tab) => {
  map[tab.key] = { enabled: false, value: tab.recommended };
  return map;
}, {});

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeTargets = source => {
  const normalized = {};
  targetTabs.forEach(tab => {
    const raw = source?.[tab.key] || {};
    const safeValue = toFiniteNumber(raw.value) ?? tab.recommended;
    const rounded =
      tab.step < 1
        ? Number(clamp(safeValue, tab.min, tab.max).toFixed(1))
        : Math.round(clamp(safeValue, tab.min, tab.max));
    normalized[tab.key] = {
      enabled: Boolean(raw.enabled),
      value: rounded,
    };
  });
  return normalized;
};

const cloneTargets = source =>
  JSON.parse(JSON.stringify(normalizeTargets(source || defaultTargets)));

const targets = ref(cloneTargets(defaultTargets));
const targetDraft = ref(cloneTargets(defaultTargets));

const loadTargets = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(TARGET_STORAGE_KEY);
    if (!raw) return;
    targets.value = cloneTargets(JSON.parse(raw));
  } catch (error) {
    targets.value = cloneTargets(defaultTargets);
  }
};

const persistTargets = nextTargets => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TARGET_STORAGE_KEY, JSON.stringify(nextTargets));
};

const normalizeVisibleColumns = source => {
  const next = { ...defaultVisibleColumns };
  if (!source || typeof source !== 'object') return next;
  Object.keys(defaultVisibleColumns).forEach(key => {
    if (typeof source[key] === 'boolean') {
      next[key] = source[key];
    }
  });
  return next;
};

const loadVisibleColumns = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    if (!raw) return;
    visibleColumns.value = normalizeVisibleColumns(JSON.parse(raw));
  } catch {
    visibleColumns.value = { ...defaultVisibleColumns };
  }
};

const persistVisibleColumns = nextColumns => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(nextColumns));
};

const toggleColumnVisibility = key => {
  visibleColumns.value = {
    ...visibleColumns.value,
    [key]: !visibleColumns.value[key],
  };
  persistVisibleColumns(visibleColumns.value);
};

const resetVisibleColumns = () => {
  visibleColumns.value = { ...defaultVisibleColumns };
  persistVisibleColumns(visibleColumns.value);
};

const agents = useMapGetter('agents/getAgents');
const metrics = useMapGetter('summaryReports/getAgentSummaryReports');
const accountSummary = useMapGetter('getAccountSummary');
const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isFetching = computed(() => uiFlags.value?.isFetchingAgentSummaryReports);

const onDateFilterChange = ({ from, to }) => {
  if (!from || !to) return;
  since.value = from;
  until.value = to;
};

const toggleFilterMenu = () => {
  showFilterMenu.value = !showFilterMenu.value;
  showColumnsMenu.value = false;
};

const toggleColumnsMenu = () => {
  showColumnsMenu.value = !showColumnsMenu.value;
  showFilterMenu.value = false;
};

const clearExtraFilters = () => {
  businessHours.value = false;
  excludeIssueTime.value = false;
};

const openTargetModal = () => {
  targetDraft.value = cloneTargets(targets.value);
  activeTargetTab.value = 'csat';
  showTargetModal.value = true;
};

const closeTargetModal = () => {
  showTargetModal.value = false;
};

const saveTargets = () => {
  const normalized = cloneTargets(targetDraft.value);
  targets.value = normalized;
  persistTargets(normalized);
  showTargetModal.value = false;
};

const setCurrentTargetToRecommended = () => {
  const tabMeta = targetMetaByKey[activeTargetTab.value];
  if (!tabMeta) return;
  targetDraft.value[activeTargetTab.value].value = tabMeta.recommended;
};

const adjustCurrentTarget = direction => {
  const tabMeta = targetMetaByKey[activeTargetTab.value];
  if (!tabMeta) return;

  const current = toFiniteNumber(
    targetDraft.value[activeTargetTab.value]?.value
  );
  const base = current == null ? tabMeta.recommended : current;
  const next = clamp(base + tabMeta.step * direction, tabMeta.min, tabMeta.max);
  targetDraft.value[activeTargetTab.value].value =
    tabMeta.step < 1 ? Number(next.toFixed(1)) : Math.round(next);
};

const fetchData = async () => {
  errorMessage.value = '';
  const [agentsOk, summaryOk, accountSummaryOk] = await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('summaryReports/fetchAgentSummaryReports', {
      since: since.value,
      until: until.value,
      businessHours: businessHours.value,
      excludeIssueTime: excludeIssueTime.value,
    }),
    store.dispatch('fetchAccountSummary', {
      from: since.value,
      to: until.value,
      type: 'account',
      groupBy: 'day',
      businessHours: businessHours.value,
      excludeIssueTime: excludeIssueTime.value,
    }),
  ]);

  if (
    [agentsOk, summaryOk, accountSummaryOk].some(result => result === false)
  ) {
    errorMessage.value = 'Unable to load report data. Please try again.';
  }
};

const handleDocumentClick = event => {
  const target = event.target;
  if (
    showFilterMenu.value &&
    filterMenuRef.value &&
    !filterMenuRef.value.contains(target)
  ) {
    showFilterMenu.value = false;
  }
  if (
    showColumnsMenu.value &&
    columnsMenuRef.value &&
    !columnsMenuRef.value.contains(target)
  ) {
    showColumnsMenu.value = false;
  }
};

const handleEscape = event => {
  if (event.key !== 'Escape') return;
  showFilterMenu.value = false;
  showColumnsMenu.value = false;
  showTargetModal.value = false;
};

onMounted(() => {
  loadTargets();
  loadVisibleColumns();
  fetchData();
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleEscape);
});

watch([since, until, businessHours, excludeIssueTime], fetchData);

const totalChats = computed(
  () =>
    pickMetricNumber(
      accountSummary.value,
      ['conversations_count', 'conversationsCount'],
      {
        fallback: 0,
      }
    ) ?? 0
);
const prevChats = computed(
  () =>
    pickMetricNumber(
      accountSummary.value?.previous,
      ['conversations_count', 'conversationsCount'],
      { fallback: 0 }
    ) ?? 0
);
const chatsDiffPct = computed(() => {
  if (!prevChats.value) return 0;
  return Math.round(
    ((totalChats.value - prevChats.value) / prevChats.value) * 100
  );
});

const averageCurrentCsat = computed(() => {
  const values = (metrics.value || [])
    .map(item =>
      pickMetricNumber(item, ['csatSurveyScore', 'csat_survey_score'], {
        transform: toPercent,
      })
    )
    .filter(value => value != null);

  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
});

const averagePreviousCsat = computed(() => {
  const values = (metrics.value || [])
    .map(item =>
      pickMetricNumber(
        item,
        ['previousCsatSurveyScore', 'previous_csat_survey_score'],
        {
          transform: toPercent,
        }
      )
    )
    .filter(value => value != null);

  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
});

const csat = computed(() => {
  const direct = pickMetricNumber(accountSummary.value, ['csat_survey_score'], {
    transform: toPercent,
  });
  return direct ?? averageCurrentCsat.value;
});

const prevCsat = computed(() => {
  const direct = pickMetricNumber(
    accountSummary.value?.previous,
    ['csat_survey_score'],
    {
      transform: toPercent,
    }
  );
  return direct ?? averagePreviousCsat.value;
});

const csatDiff = computed(() => {
  if (csat.value == null || prevCsat.value == null) return null;
  return Math.round((csat.value - prevCsat.value) * 10) / 10;
});

const averageCurrentFRT = computed(() => {
  const values = (metrics.value || [])
    .map(item =>
      pickMetricNumber(item, [
        'avgFirstResponseTime',
        'avg_first_response_time',
      ])
    )
    .filter(value => value != null);

  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
});

const averagePreviousFRT = computed(() => {
  const values = (metrics.value || [])
    .map(item =>
      pickMetricNumber(item, [
        'previousAvgFirstResponseTime',
        'previous_avg_first_response_time',
      ])
    )
    .filter(value => value != null);

  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
});

const avgFRT = computed(() => {
  const direct = pickMetricNumber(accountSummary.value, [
    'avg_first_response_time',
    'avgFirstResponseTime',
  ]);
  return direct ?? averageCurrentFRT.value;
});

const prevFRT = computed(() => {
  const direct = pickMetricNumber(accountSummary.value?.previous, [
    'avg_first_response_time',
    'avgFirstResponseTime',
  ]);
  return direct ?? averagePreviousFRT.value;
});

const frtDiff = computed(() => {
  if (avgFRT.value == null || prevFRT.value == null) return null;
  return Math.round(avgFRT.value - prevFRT.value);
});

const aggregateEfficiency = (conversationsKeys = [], chattingTimeKeys = []) => {
  const totals = (metrics.value || []).reduce(
    (acc, item) => {
      const conversations =
        pickMetricNumber(item, conversationsKeys, { fallback: 0 }) ?? 0;
      const chattingTime =
        pickMetricNumber(item, chattingTimeKeys, { fallback: 0 }) ?? 0;

      acc.conversations += Number(conversations) || 0;
      acc.chattingTime += Number(chattingTime) || 0;
      return acc;
    },
    { conversations: 0, chattingTime: 0 }
  );

  const hours = totals.chattingTime / 3600;
  if (hours <= 0) return null;

  return Math.round((totals.conversations / hours) * 10) / 10;
};

const summaryEfficiency = computed(() => {
  const direct = pickMetricNumber(accountSummary.value, ['efficiency']);
  if (direct != null) return direct;
  return aggregateEfficiency(
    ['conversationsCount', 'conversations_count'],
    ['chattingTime', 'chatting_time']
  );
});

const summaryPrevEfficiency = computed(() => {
  const direct = pickMetricNumber(accountSummary.value?.previous, [
    'efficiency',
  ]);
  if (direct != null) return direct;
  return aggregateEfficiency(
    ['previousConversationsCount', 'previous_conversations_count'],
    ['previousChattingTime', 'previous_chatting_time']
  );
});

const efficiencyDiff = computed(() => {
  if (summaryEfficiency.value == null || summaryPrevEfficiency.value == null)
    return null;
  return (
    Math.round((summaryEfficiency.value - summaryPrevEfficiency.value) * 10) /
    10
  );
});

const fmtSec = value => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  const sec = Math.round(Math.abs(safe));
  if (sec < 60) return `${sec}s`;
  const mins = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${mins}min ${rem}s`;
};

const fmtDiffSec = value => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  const sec = Math.round(Math.abs(safe));
  if (sec < 60) return `${sec} s`;
  const mins = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${mins} min ${rem} s`;
};

const fmtTime = value => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  return formatTime(safe);
};

const fmtNum = value =>
  toFiniteNumber(value) == null ? '--' : Number(value).toLocaleString();

const fmtDelta = value => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  const abs = Math.abs(safe);
  return Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
};

const fmtTargetValue = (key, value) => {
  const safe = toFiniteNumber(value);
  if (safe == null) return '--';
  if (key === 'csat') return `${Math.round(safe)}%`;
  if (key === 'frt') return fmtSec(safe);
  return safe.toFixed(1);
};

const byId = computed(() => {
  const map = {};
  (metrics.value || []).forEach(item => {
    map[item.id] = item;
  });
  return map;
});

const rows = computed(() => {
  let list = (agents.value || []).map(agent => {
    const metric = byId.value[agent.id] || {};
    const convs =
      pickMetricNumber(metric, ['conversationsCount', 'conversations_count'], {
        fallback: 0,
      }) ?? 0;
    const prevConvs =
      pickMetricNumber(
        metric,
        ['previousConversationsCount', 'previous_conversations_count'],
        { fallback: 0 }
      ) ?? 0;
    const sat = pickMetricNumber(
      metric,
      ['csatSurveyScore', 'csat_survey_score'],
      {
        transform: toPercent,
      }
    );
    const prevSat = pickMetricNumber(
      metric,
      ['previousCsatSurveyScore', 'previous_csat_survey_score'],
      { transform: toPercent }
    );
    const frt = pickMetricNumber(metric, [
      'avgFirstResponseTime',
      'avg_first_response_time',
    ]);
    const prevFrt = pickMetricNumber(metric, [
      'previousAvgFirstResponseTime',
      'previous_avg_first_response_time',
    ]);
    const chatTime = pickMetricNumber(metric, [
      'chattingTime',
      'chatting_time',
    ]);
    const prevChatTime = pickMetricNumber(metric, [
      'previousChattingTime',
      'previous_chatting_time',
    ]);
    const eff = pickMetricNumber(metric, ['efficiency']);
    const prevEff = pickMetricNumber(metric, ['previousEfficiency']);
    const avgResolutionTime = pickMetricNumber(metric, [
      'avgResolutionTime',
      'avg_resolution_time',
    ]);
    const prevAvgResolutionTime = pickMetricNumber(metric, [
      'previousAvgResolutionTime',
      'previous_avg_resolution_time',
    ]);
    const avgReplyTime = pickMetricNumber(metric, [
      'avgReplyTime',
      'avg_reply_time',
    ]);
    const prevAvgReplyTime = pickMetricNumber(metric, [
      'previousAvgReplyTime',
      'previous_avg_reply_time',
    ]);
    const resolutionCount = pickMetricNumber(
      metric,
      [
        'resolvedConversationsCount',
        'resolved_conversations_count',
        'resolutionCount',
      ],
      { fallback: 0 }
    );
    const prevResolutionCount =
      pickMetricNumber(
        metric,
        [
          'previousResolvedConversationsCount',
          'previous_resolved_conversations_count',
          'previousResolutionCount',
          'previous_resolution_count',
        ],
        { fallback: 0 }
      ) ?? 0;

    return {
      id: agent.id,
      name: agent.name,
      email: agent.email,
      avatar: agent.thumbnail,
      conversations: convs,
      convsDiff: convs - prevConvs,
      satisfaction: sat,
      satDiff:
        sat != null && prevSat != null
          ? Math.round((sat - prevSat) * 10) / 10
          : null,
      firstResp: frt,
      frtDiff:
        frt != null && prevFrt != null ? Math.round(frt - prevFrt) : null,
      chattingTime: chatTime,
      ctDiff:
        chatTime != null && prevChatTime != null
          ? chatTime - prevChatTime
          : null,
      efficiency: eff,
      effDiff:
        eff != null && prevEff != null
          ? Math.round((eff - prevEff) * 10) / 10
          : null,
      avgResolutionTime,
      avgResolutionTimeDiff:
        avgResolutionTime != null && prevAvgResolutionTime != null
          ? Math.round(avgResolutionTime - prevAvgResolutionTime)
          : null,
      avgReplyTime,
      avgReplyTimeDiff:
        avgReplyTime != null && prevAvgReplyTime != null
          ? Math.round(avgReplyTime - prevAvgReplyTime)
          : null,
      resolutionCount,
      resolutionCountDiff:
        Number(resolutionCount || 0) - Number(prevResolutionCount || 0),
    };
  });

  if (hideInactive.value) {
    list = list.filter(row => row.conversations > 0);
  }

  const col = sortCol.value;
  list.sort((a, b) => {
    const va = a[col] ?? 0;
    const vb = b[col] ?? 0;
    return sortDesc.value ? vb - va : va - vb;
  });

  return list;
});

const chartRows = computed(() => rows.value.slice(0, 8));
const hasChartData = computed(() => chartRows.value.length > 0);

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row => row.name || '--'),
  datasets: [
    {
      label: 'Total chats',
      data: chartRows.value.map(row => Number(row.conversations || 0)),
      backgroundColor: '#1d75e5',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      ticks: {
        maxRotation: 0,
        minRotation: 0,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
};

const toggleSort = col => {
  if (sortCol.value === col) {
    sortDesc.value = !sortDesc.value;
    return;
  }
  sortCol.value = col;
  sortDesc.value = true;
};

const cellClass = (value, invert = false) => {
  if (value == null || value === 0) return 'cell-neutral';
  const isPositive = invert ? value < 0 : value > 0;
  const abs = Math.abs(value);

  let level = 'lowest';
  if (abs > 50) level = 'high';
  else if (abs > 20) level = 'medium';
  else if (abs > 5) level = 'low';

  return isPositive ? `cell-positive-${level}` : `cell-negative-${level}`;
};

const evaluateTarget = (key, currentValue) => {
  const config = targets.value[key] || defaultTargets[key];
  if (!config?.enabled) {
    return {
      enabled: false,
      achieved: false,
      value: config?.value,
    };
  }

  const safeCurrent = toFiniteNumber(currentValue);
  if (safeCurrent == null) {
    return {
      enabled: true,
      achieved: false,
      value: config.value,
    };
  }

  const meta = targetMetaByKey[key];
  const achieved =
    meta?.targetDirection === 'lower'
      ? safeCurrent <= config.value
      : safeCurrent >= config.value;

  return {
    enabled: true,
    achieved,
    value: config.value,
  };
};

const targetStates = computed(() => ({
  csat: evaluateTarget('csat', csat.value),
  frt: evaluateTarget('frt', avgFRT.value),
  efficiency: evaluateTarget('efficiency', summaryEfficiency.value),
}));

const extraFiltersCount = computed(
  () => [businessHours.value, excludeIssueTime.value].filter(Boolean).length
);
const optionalMetricColumnsCount = computed(
  () =>
    Object.keys(defaultVisibleColumns).filter(key => visibleColumns.value[key])
      .length
);
const tableColumnCount = computed(() => 2 + optionalMetricColumnsCount.value);
const activeTargetMeta = computed(
  () => targetMetaByKey[activeTargetTab.value] || targetTabs[0]
);

const onDownload = () => {
  const fileName = generateFileName({
    type: 'agent',
    to: until.value,
    businessHours: businessHours.value,
  });
  store.dispatch('downloadAgentReports', {
    from: since.value,
    to: until.value,
    fileName,
    businessHours: businessHours.value,
    excludeIssueTime: excludeIssueTime.value,
  });
};
</script>

<template>
  <div class="ap-page">
    <div class="ap-panel">
      <div class="ap-hdr">
        <div class="ap-hdr-left">
          <h1 class="ap-hdr-title">Agent performance</h1>
        </div>
      </div>

      <div class="ap-body">
        <div class="ap-filter-row">
          <div class="ap-filter-left">
            <div ref="filterMenuRef" class="ap-filter-anchor">
              <button
                class="ap-secondary-btn"
                type="button"
                @click.stop="toggleFilterMenu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 12h14m-7-7v14"
                  />
                </svg>
                <span>Add filter</span>
                <span v-if="extraFiltersCount" class="ap-filter-count">
                  {{ extraFiltersCount }}
                </span>
              </button>
              <div v-if="showFilterMenu" class="ap-filter-menu" @click.stop>
                <label class="ap-filter-menu__item">
                  <input v-model="businessHours" type="checkbox" />
                  <span>Business hours only</span>
                </label>
                <label class="ap-filter-menu__item">
                  <input v-model="excludeIssueTime" type="checkbox" />
                  <span>Exclude issue time</span>
                </label>
                <div class="ap-filter-menu__footer">
                  <button
                    class="ap-filter-menu__btn"
                    type="button"
                    @click="clearExtraFilters"
                  >
                    Clear
                  </button>
                  <button
                    class="ap-filter-menu__btn"
                    type="button"
                    @click="showFilterMenu = false"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>

            <div class="ap-date-nav">
              <CompactDateInboxFilters
                :show-add-filter="false"
                :show-inbox="false"
                :emit-initial-change="false"
                :forced-from="since"
                :forced-to="until"
                @filter-change="onDateFilterChange"
              />
            </div>
          </div>

          <button
            class="ap-square-btn"
            type="button"
            aria-label="Target configuration"
            @click="openTargetModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="18"
              height="18"
            >
              <path
                stroke="currentcolor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 12h10m-10 0a2 2 0 0 1-4 0m4 0a2 2 0 0 0-4 0m0 0H4m11 6a2 2 0 0 0 4 0m-4 0a2 2 0 0 1 4 0m-4 0H4m15 0h1M12 6a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m-4 0H4m12 0h4"
              />
            </svg>
          </button>
        </div>
        <div v-if="errorMessage" class="ap-error">
          {{ errorMessage }}
        </div>

        <div class="ap-cards">
          <div class="ap-card">
            <div class="ap-card-label">Total chats</div>
            <div class="ap-card-main">
              <span class="ap-card-value">{{
                totalChats.toLocaleString()
              }}</span>
              <span
                v-if="prevChats || chatsDiffPct !== 0"
                class="ap-pill"
                :class="chatsDiffPct < 0 ? 'ap-pill--red' : 'ap-pill--green'"
              >
                <svg
                  v-if="chatsDiffPct < 0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19V5m0 14 4-4m-4 4-4-4"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m0-14L8 9m4-4 4 4"
                  />
                </svg>
                {{ fmtDelta(chatsDiffPct) }}%
              </span>
            </div>
          </div>

          <div class="ap-card">
            <div class="ap-card-label">Chat satisfaction</div>
            <div class="ap-card-main">
              <span class="ap-card-value">{{
                csat != null ? `${Math.round(csat)}%` : '--'
              }}</span>
              <span
                v-if="csatDiff != null"
                class="ap-pill"
                :class="csatDiff < 0 ? 'ap-pill--red' : 'ap-pill--green'"
              >
                <svg
                  v-if="csatDiff < 0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19V5m0 14 4-4m-4 4-4-4"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m0-14L8 9m4-4 4 4"
                  />
                </svg>
                {{ fmtDelta(csatDiff) }} p.p
              </span>
            </div>
            <div
              class="ap-target"
              :class="{ 'ap-target--achieved': targetStates.csat.achieved }"
            >
              <svg
                class="ap-target-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m14.75 10-3 3m3-3V7l3-3v3h3l-3 3zm-3-2a5 5 0 1 0 5 5m-4-8.945A9 9 0 1 0 20.691 12m-9.941 1a1 1 0 1 0 2 0 1 1 0 0 0-2 0"
                />
              </svg>
              <template v-if="!targetStates.csat.enabled">
                <span>No target set</span>
              </template>
              <template v-else-if="targetStates.csat.achieved">
                <p class="ap-target-copy">
                  {{ fmtTargetValue('csat', targetStates.csat.value) }}
                </p>
                <p class="ap-target-copy">Target achieved 🎉</p>
              </template>
              <template v-else>
                <span>{{
                  fmtTargetValue('csat', targetStates.csat.value)
                }}</span>
              </template>
            </div>
          </div>

          <div class="ap-card">
            <div class="ap-card-label">First response time</div>
            <div class="ap-card-main">
              <span class="ap-card-value">{{
                avgFRT != null ? fmtSec(avgFRT) : '--'
              }}</span>
              <span
                v-if="frtDiff != null"
                class="ap-pill"
                :class="frtDiff > 0 ? 'ap-pill--red' : 'ap-pill--green'"
              >
                <svg
                  v-if="frtDiff > 0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m0-14L8 9m4-4 4 4"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19V5m0 14 4-4m-4 4-4-4"
                  />
                </svg>
                {{ fmtDiffSec(frtDiff) }}
              </span>
            </div>
            <div
              class="ap-target"
              :class="{ 'ap-target--achieved': targetStates.frt.achieved }"
            >
              <svg
                class="ap-target-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m14.75 10-3 3m3-3V7l3-3v3h3l-3 3zm-3-2a5 5 0 1 0 5 5m-4-8.945A9 9 0 1 0 20.691 12m-9.941 1a1 1 0 1 0 2 0 1 1 0 0 0-2 0"
                />
              </svg>
              <template v-if="!targetStates.frt.enabled">
                <span>No target set</span>
              </template>
              <template v-else-if="targetStates.frt.achieved">
                <p class="ap-target-copy">
                  {{ fmtTargetValue('frt', targetStates.frt.value) }}
                </p>
                <p class="ap-target-copy">Target achieved 🎉</p>
              </template>
              <template v-else>
                <span>{{ fmtTargetValue('frt', targetStates.frt.value) }}</span>
              </template>
            </div>
          </div>

          <div class="ap-card">
            <div class="ap-card-label">
              <span>Efficiency</span>
              <span class="ap-info-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                  />
                </svg>
              </span>
            </div>
            <div class="ap-card-main">
              <span class="ap-card-value">
                {{
                  summaryEfficiency != null
                    ? summaryEfficiency.toFixed(1)
                    : '--'
                }}
              </span>
              <span
                v-if="efficiencyDiff != null"
                class="ap-pill"
                :class="efficiencyDiff < 0 ? 'ap-pill--red' : 'ap-pill--green'"
              >
                <svg
                  v-if="efficiencyDiff < 0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19V5m0 14 4-4m-4 4-4-4"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 5v14m0-14L8 9m4-4 4 4"
                  />
                </svg>
                {{ fmtDelta(efficiencyDiff) }}
              </span>
            </div>
            <div
              class="ap-target"
              :class="{
                'ap-target--achieved': targetStates.efficiency.achieved,
              }"
            >
              <svg
                class="ap-target-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m14.75 10-3 3m3-3V7l3-3v3h3l-3 3zm-3-2a5 5 0 1 0 5 5m-4-8.945A9 9 0 1 0 20.691 12m-9.941 1a1 1 0 1 0 2 0 1 1 0 0 0-2 0"
                />
              </svg>
              <template v-if="!targetStates.efficiency.enabled">
                <span>No target set</span>
              </template>
              <template v-else-if="targetStates.efficiency.achieved">
                <p class="ap-target-copy">
                  {{
                    fmtTargetValue('efficiency', targetStates.efficiency.value)
                  }}
                </p>
                <p class="ap-target-copy">Target achieved 🎉</p>
              </template>
              <template v-else>
                <span>{{
                  fmtTargetValue('efficiency', targetStates.efficiency.value)
                }}</span>
              </template>
            </div>
          </div>
        </div>

        <div class="ap-detail-header">
          <h2 class="ap-detail-title">Detailed report</h2>
          <div class="ap-detail-actions">
            <label class="ap-switch">
              <span
                class="ap-switch-track"
                :class="{ 'ap-switch-track--on': hideInactive }"
              >
                <input
                  v-model="hideInactive"
                  type="checkbox"
                  class="ap-switch-input"
                />
                <span class="ap-switch-thumb" />
              </span>
              <span>Hide inactive agents</span>
            </label>

            <div ref="columnsMenuRef" class="ap-columns-anchor">
              <button
                class="ap-square-btn"
                type="button"
                aria-label="Columns configurator"
                @click.stop="toggleColumnsMenu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                >
                  <path
                    stroke="currentcolor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 3v18m6-18v18M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"
                  />
                </svg>
              </button>
              <div v-if="showColumnsMenu" class="ap-columns-menu" @click.stop>
                <div class="ap-columns-menu__title">Columns</div>
                <label
                  v-for="column in columnOptions"
                  :key="column.key"
                  class="ap-columns-menu__item"
                >
                  <input
                    :checked="visibleColumns[column.key]"
                    type="checkbox"
                    @change="toggleColumnVisibility(column.key)"
                  />
                  <span>{{ column.label }}</span>
                </label>
                <div class="ap-columns-menu__footer">
                  <button
                    class="ap-columns-menu__btn"
                    type="button"
                    @click="resetVisibleColumns"
                  >
                    Reset
                  </button>
                  <button
                    class="ap-columns-menu__btn"
                    type="button"
                    @click="showColumnsMenu = false"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
            <button
              class="ap-square-btn"
              type="button"
              aria-label="Export CSV"
              @click="onDownload"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="ap-table-scroll">
          <table class="ap-tbl">
            <thead>
              <tr>
                <th class="ap-th ap-th--agent ap-sticky">Agent</th>
                <th
                  class="ap-th ap-th--metric ap-th--sortable"
                  @click="toggleSort('conversations')"
                >
                  <button class="ap-th-btn" type="button">
                    <span class="ap-sort-arrow">{{
                      sortDesc ? '▼' : '▲'
                    }}</span>
                    <span>Total chats</span>
                  </button>
                </th>
                <th
                  v-if="visibleColumns.satisfaction"
                  class="ap-th ap-th--metric"
                >
                  Satisfaction
                </th>
                <th v-if="visibleColumns.firstResp" class="ap-th ap-th--metric">
                  First resp.
                </th>
                <th
                  v-if="visibleColumns.chattingTime"
                  class="ap-th ap-th--metric"
                >
                  <span>Chatting time</span>
                  <span class="ap-info-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                    >
                      <path
                        stroke="currentcolor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                      />
                    </svg>
                  </span>
                </th>
                <th
                  v-if="visibleColumns.efficiency"
                  class="ap-th ap-th--metric"
                >
                  <span>Efficiency</span>
                  <span class="ap-info-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                    >
                      <path
                        stroke="currentcolor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                      />
                    </svg>
                  </span>
                </th>
                <th
                  v-if="visibleColumns.avgResolutionTime"
                  class="ap-th ap-th--metric"
                >
                  Avg. Resolution Time
                </th>
                <th
                  v-if="visibleColumns.avgCustomerWaitingTime"
                  class="ap-th ap-th--metric"
                >
                  Avg. Customer Waiting Time
                </th>
                <th
                  v-if="visibleColumns.resolutionCount"
                  class="ap-th ap-th--metric"
                >
                  Resolution Count
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isFetching">
                <td :colspan="tableColumnCount" class="ap-td ap-td--empty">
                  <div class="ap-spinner" />
                </td>
              </tr>
              <tr v-else-if="!rows.length">
                <td :colspan="tableColumnCount" class="ap-td ap-td--empty">
                  No agents found.
                </td>
              </tr>
              <template v-else>
                <tr v-for="row in rows" :key="row.id" class="ap-row">
                  <td class="ap-td ap-td--agent ap-sticky">
                    <div class="ap-agent">
                      <div class="ap-avatar">
                        <img
                          v-if="row.avatar"
                          :src="row.avatar"
                          class="ap-avatar__img"
                          loading="lazy"
                        />
                        <span v-else class="ap-avatar__ini">{{
                          (row.name || '?')[0]
                        }}</span>
                      </div>
                      <div class="ap-agent__info">
                        <span class="ap-agent__name">{{ row.name }}</span>
                        <span class="ap-agent__email">{{ row.email }}</span>
                      </div>
                    </div>
                  </td>

                  <td class="ap-td ap-td--metric">
                    <div
                      class="ap-metric-cell"
                      :class="cellClass(row.convsDiff)"
                    >
                      <div class="ap-cell-value">
                        {{ fmtNum(row.conversations) }}
                      </div>
                      <div
                        v-if="row.convsDiff !== 0"
                        class="ap-cell-diff"
                        :class="
                          row.convsDiff > 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.convsDiff > 0 ? '+' : '-'
                        }}{{ fmtNum(Math.abs(row.convsDiff)) }}
                      </div>
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.satisfaction"
                    class="ap-td ap-td--metric"
                  >
                    <div class="ap-metric-cell" :class="cellClass(row.satDiff)">
                      <div class="ap-cell-value">
                        {{
                          row.satisfaction != null
                            ? `${Math.round(row.satisfaction * 10) / 10}%`
                            : '--'
                        }}
                      </div>
                      <div
                        v-if="row.satDiff != null && row.satDiff !== 0"
                        class="ap-cell-diff"
                        :class="
                          row.satDiff > 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.satDiff > 0 ? '+' : '-'
                        }}{{ fmtDelta(row.satDiff) }}pct pts
                      </div>
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.firstResp"
                    class="ap-td ap-td--metric"
                  >
                    <div
                      class="ap-metric-cell"
                      :class="cellClass(row.frtDiff, true)"
                    >
                      <div class="ap-cell-value">
                        {{ fmtSec(row.firstResp) }}
                      </div>
                      <div
                        v-if="row.frtDiff != null && row.frtDiff !== 0"
                        class="ap-cell-diff"
                        :class="
                          row.frtDiff < 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.frtDiff > 0 ? '+' : '-'
                        }}{{ fmtDiffSec(row.frtDiff) }}
                      </div>
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.chattingTime"
                    class="ap-td ap-td--metric"
                  >
                    <div class="ap-metric-cell" :class="cellClass(row.ctDiff)">
                      <div class="ap-cell-value">
                        {{ fmtTime(row.chattingTime) }}
                      </div>
                      <div
                        v-if="row.ctDiff != null && row.ctDiff !== 0"
                        class="ap-cell-diff"
                        :class="
                          row.ctDiff > 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.ctDiff > 0 ? '+' : '-'
                        }}{{ fmtTime(Math.abs(row.ctDiff)) }}
                      </div>
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.efficiency"
                    class="ap-td ap-td--metric"
                  >
                    <div class="ap-metric-cell" :class="cellClass(row.effDiff)">
                      <div class="ap-cell-value">
                        {{
                          row.efficiency != null
                            ? row.efficiency.toFixed(1)
                            : '--'
                        }}
                      </div>
                      <div
                        v-if="row.effDiff != null && row.effDiff !== 0"
                        class="ap-cell-diff"
                        :class="
                          row.effDiff > 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.effDiff > 0 ? '+' : '-'
                        }}{{ fmtDelta(row.effDiff) }}
                      </div>
                    </div>
                  </td>

                  <td
                    v-if="visibleColumns.avgResolutionTime"
                    class="ap-td ap-td--metric"
                  >
                    <div
                      class="ap-metric-cell"
                      :class="cellClass(row.avgResolutionTimeDiff, true)"
                    >
                      <div class="ap-cell-value">
                        {{ fmtTime(row.avgResolutionTime) }}
                      </div>
                      <div
                        v-if="
                          row.avgResolutionTimeDiff != null &&
                          row.avgResolutionTimeDiff !== 0
                        "
                        class="ap-cell-diff"
                        :class="
                          row.avgResolutionTimeDiff < 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.avgResolutionTimeDiff > 0 ? '+' : '-'
                        }}{{ fmtTime(Math.abs(row.avgResolutionTimeDiff)) }}
                      </div>
                    </div>
                  </td>
                  <td
                    v-if="visibleColumns.avgCustomerWaitingTime"
                    class="ap-td ap-td--metric"
                  >
                    <div
                      class="ap-metric-cell"
                      :class="cellClass(row.avgReplyTimeDiff, true)"
                    >
                      <div class="ap-cell-value">
                        {{ fmtTime(row.avgReplyTime) }}
                      </div>
                      <div
                        v-if="
                          row.avgReplyTimeDiff != null &&
                          row.avgReplyTimeDiff !== 0
                        "
                        class="ap-cell-diff"
                        :class="
                          row.avgReplyTimeDiff < 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.avgReplyTimeDiff > 0 ? '+' : '-'
                        }}{{ fmtTime(Math.abs(row.avgReplyTimeDiff)) }}
                      </div>
                    </div>
                  </td>
                  <td
                    v-if="visibleColumns.resolutionCount"
                    class="ap-td ap-td--metric"
                  >
                    <div
                      class="ap-metric-cell"
                      :class="cellClass(row.resolutionCountDiff)"
                    >
                      <div class="ap-cell-value">
                        {{ fmtNum(row.resolutionCount) }}
                      </div>
                      <div
                        v-if="
                          row.resolutionCountDiff != null &&
                          row.resolutionCountDiff !== 0
                        "
                        class="ap-cell-diff"
                        :class="
                          row.resolutionCountDiff > 0
                            ? 'ap-cell-diff--pos'
                            : 'ap-cell-diff--neg'
                        "
                      >
                        {{ row.resolutionCountDiff > 0 ? '+' : '-'
                        }}{{ fmtNum(Math.abs(row.resolutionCountDiff)) }}
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <section class="ap-chart-panel">
          <div class="ap-chart-head">
            <h2 class="ap-chart-title">Performance trend</h2>
            <p class="ap-chart-subtitle">Top 8 agents by total chats</p>
          </div>
          <div class="ap-chart-body">
            <woot-loading-state
              v-if="isFetching"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div
              v-else-if="!hasChartData"
              class="ap-chart-empty text-sm text-n-slate-11"
            >
              No chart data available for this range.
            </div>
            <BarChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showTargetModal"
        class="ap-modal-scrim"
        @click.self="closeTargetModal"
      >
        <div
          class="ap-target-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Target configuration"
        >
          <div class="ap-target-modal__header">
            <h3 class="ap-target-modal__title">Change targets</h3>
            <button
              class="ap-target-modal__close"
              type="button"
              aria-label="Close target modal"
              @click="closeTargetModal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m18 6-12 12M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="ap-target-tabs">
            <button
              v-for="tab in targetTabs"
              :key="tab.key"
              class="ap-target-tab"
              :class="{ 'ap-target-tab--active': activeTargetTab === tab.key }"
              type="button"
              @click="activeTargetTab = tab.key"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="ap-target-panel">
            <label class="ap-target-toggle">
              <span class="ap-target-toggle__text">Set target</span>
              <span
                class="ap-switch-track"
                :class="{
                  'ap-switch-track--on': targetDraft[activeTargetTab].enabled,
                }"
              >
                <input
                  :checked="targetDraft[activeTargetTab].enabled"
                  class="ap-switch-input"
                  type="checkbox"
                  @change="
                    targetDraft[activeTargetTab].enabled = $event.target.checked
                  "
                />
                <span class="ap-switch-thumb" />
              </span>
            </label>

            <p class="ap-target-help">
              Mark as achieved when
              <strong>{{ activeTargetMeta.label }}</strong>
              is
              <strong>
                {{
                  activeTargetMeta.targetDirection === 'lower'
                    ? 'less than or equal to'
                    : 'greater than or equal to'
                }}
              </strong>
              the configured value.
            </p>

            <div
              class="ap-target-input-row"
              :class="{
                'ap-target-input-row--disabled':
                  !targetDraft[activeTargetTab].enabled,
              }"
            >
              <button
                class="ap-target-stepper"
                type="button"
                :disabled="!targetDraft[activeTargetTab].enabled"
                @click="adjustCurrentTarget(-1)"
              >
                -
              </button>
              <input
                v-model.number="targetDraft[activeTargetTab].value"
                class="ap-target-input"
                type="number"
                :min="activeTargetMeta.min"
                :max="activeTargetMeta.max"
                :step="activeTargetMeta.step"
                :disabled="!targetDraft[activeTargetTab].enabled"
              />
              <span v-if="activeTargetMeta.unit" class="ap-target-unit">
                {{ activeTargetMeta.unit }}
              </span>
              <button
                class="ap-target-stepper"
                type="button"
                :disabled="!targetDraft[activeTargetTab].enabled"
                @click="adjustCurrentTarget(1)"
              >
                +
              </button>
            </div>

            <div class="ap-target-recommend">
              <span>Recommended:</span>
              <button
                class="ap-target-recommend__btn"
                type="button"
                @click="setCurrentTargetToRecommended"
              >
                {{
                  fmtTargetValue(
                    activeTargetMeta.key,
                    activeTargetMeta.recommended
                  )
                }}
              </button>
            </div>
          </div>

          <div class="ap-target-modal__footer">
            <button
              class="ap-target-action ap-target-action--ghost"
              type="button"
              @click="closeTargetModal"
            >
              Cancel
            </button>
            <button
              class="ap-target-action ap-target-action--primary"
              type="button"
              @click="saveTargets"
            >
              Save and close
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.ap-page,
:global(body.dark) .ap-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: #ffffff !important;
  color: #111827;
  overflow: auto;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.ap-page * {
  box-sizing: border-box;
}

.ap-panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background: #ffffff;
  border-top: 1px solid #eceff3;
}

.ap-hdr {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.ap-hdr-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.ap-hdr-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.ap-body {
  flex: 1;
  overflow: auto;
  background: #ffffff;
}

.ap-body::after {
  content: '';
  display: block;
  height: 64px;
}

.ap-tbl thead,
.ap-tbl thead tr {
  background: #ffffff !important;
}

.ap-filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
}

.ap-error {
  margin: 0 24px 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.ap-filter-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ap-filter-anchor,
.ap-columns-anchor {
  position: relative;
}

.ap-secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.ap-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 700;
}

.ap-filter-menu,
.ap-columns-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 240px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 35;
}

.ap-filter-menu__item,
.ap-columns-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 4px 4px;
  font-size: 13px;
  color: #111827;
}

.ap-filter-menu__footer,
.ap-columns-menu__footer {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.ap-filter-menu__btn,
.ap-columns-menu__btn {
  height: 30px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  padding: 0 10px;
  cursor: pointer;
}

.ap-columns-menu__title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.ap-columns-anchor .ap-columns-menu {
  left: auto;
  right: 0;
}

.ap-date-nav {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  position: relative;
  height: 34px;
  padding: 2px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  background: #f3f4f6;
}

.ap-nav-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ap-nav-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.ap-date-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ap-date-chip strong {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ap-date-chip:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.ap-date-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #111827;
  font-size: 11px;
  font-weight: 700;
}

.ap-date-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 240px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 30;
}

.ap-date-menu__title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.ap-date-menu__item {
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.ap-date-menu__item:hover {
  background: #f3f4f6;
}

.ap-date-menu__item--active {
  background: #eef2ff;
  color: #1d4ed8;
  font-weight: 600;
}

.ap-date-menu__divider {
  border-top: 1px solid #eef0f3;
  margin: 8px 0;
}

.ap-date-menu__check {
  display: inline-flex;
  align-items: center;
  color: currentcolor;
}

.ap-date-custom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.ap-date-custom__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
}

.ap-date-custom__field input {
  height: 30px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  padding: 0 8px;
  color: #111827;
  font-size: 12px;
}

.ap-date-error {
  margin: 8px 4px 0;
  font-size: 11px;
  color: #dc2626;
}

.ap-date-menu__apply {
  width: 100%;
  height: 30px;
  margin-top: 8px;
  border: 1px solid #111827;
  border-radius: 8px;
  background: #111827;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.ap-date-menu__footer {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.ap-date-menu__done {
  height: 30px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  padding: 0 10px;
  cursor: pointer;
}

.ap-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.ap-square-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ap-square-btn:hover {
  background: #f9fafb;
}

.ap-modal-scrim {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(17, 24, 39, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.ap-target-modal {
  width: min(560px, 100%);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 22px 44px rgba(17, 24, 39, 0.22);
  overflow: hidden;
}

.ap-target-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f3;
}

.ap-target-modal__title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  color: #111827;
}

.ap-target-modal__close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ap-target-tabs {
  display: flex;
  gap: 6px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef0f3;
  overflow-x: auto;
}

.ap-target-tab {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 7px 12px;
  cursor: pointer;
  white-space: nowrap;
}

.ap-target-tab--active {
  border-color: #111827;
  background: #111827;
  color: #ffffff;
}

.ap-target-panel {
  padding: 14px 16px 18px;
}

.ap-target-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ap-target-toggle__text {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.ap-target-help {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7280;
}

.ap-target-help strong {
  color: #111827;
}

.ap-target-input-row {
  margin-top: 12px;
  border: 1px solid #d9dde3;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  align-items: center;
  padding: 4px;
  gap: 6px;
}

.ap-target-input-row--disabled {
  opacity: 0.55;
}

.ap-target-stepper {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.ap-target-stepper:disabled {
  cursor: not-allowed;
}

.ap-target-input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

.ap-target-unit {
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  padding-right: 2px;
}

.ap-target-recommend {
  margin-top: 10px;
  font-size: 12px;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ap-target-recommend__btn {
  border: 1px dashed #d1d5db;
  background: #ffffff;
  color: #111827;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  cursor: pointer;
}

.ap-target-modal__footer {
  border-top: 1px solid #eef0f3;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.ap-target-action {
  min-height: 34px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  cursor: pointer;
}

.ap-target-action--ghost {
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
}

.ap-target-action--primary {
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
}

.ap-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 10px 24px 16px;
}

.ap-chart-panel {
  margin: 30px 24px 10px;
  border: 1px solid #ebedf0;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
}

.ap-chart-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  padding: 12px 14px 8px;
  border-bottom: 1px solid #f3f4f6;
}

.ap-chart-title {
  margin: 0;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 600;
  color: #111827;
}

.ap-chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.ap-chart-body {
  height: 280px;
  padding: 10px 14px 14px;
}

.ap-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.ap-card {
  display: flex;
  flex-direction: column;
  min-height: 124px;
  padding: 14px 14px 12px;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  background: #f3f4f6;
}

.ap-card-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.3;
  color: #111827;
}

.ap-card-main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 56px;
  margin-top: 8px;
}

.ap-card-value {
  font-size: 32px;
  line-height: 1.05;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.03em;
  white-space: nowrap;
}

.ap-pill {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.ap-pill--green {
  color: #157f3f;
  background: #e8f7ed;
}

.ap-pill--red {
  color: #b42318;
  background: #fdebec;
}

.ap-target {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
}

.ap-target-copy {
  margin: 0;
}

.ap-target--achieved {
  justify-content: center;
  color: #157f3f;
  gap: 6px;
}

.ap-target-icon {
  width: 16px;
  height: 16px;
  color: currentcolor;
  flex-shrink: 0;
}

.ap-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 24px 8px;
}

.ap-detail-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  letter-spacing: 0;
  color: #111827;
}

.ap-detail-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.ap-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  user-select: none;
}

.ap-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ap-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  box-sizing: border-box;
  background: rgb(221, 221, 221);
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
}

.ap-switch-track--on {
  background: #111827;
}

.ap-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgb(255, 255, 255);
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition:
    left 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  will-change: left, background-color;
}

.ap-switch-track--on .ap-switch-thumb {
  left: 10px;
}

.ap-table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 24px 16px;
  scrollbar-width: thin;
  scrollbar-color: #cfd4dc transparent;
}

.ap-table-scroll::-webkit-scrollbar {
  height: 8px;
}

.ap-table-scroll::-webkit-scrollbar-thumb {
  background: #cfd4dc;
  border-radius: 999px;
}

.ap-tbl {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  background: #ffffff;
}

.ap-th {
  padding: 10px 10px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 500;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.ap-th,
.ap-th * {
  color: #111827 !important;
}

.ap-th--agent {
  min-width: 264px;
  padding-left: 8px;
}

.ap-th--metric {
  min-width: 134px;
  text-align: center;
}

.ap-th--sortable {
  cursor: pointer;
}

.ap-th-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.ap-sort-arrow {
  font-size: 10px;
  color: #6b7280;
}

.ap-info-icon {
  display: inline-flex;
  width: 16px;
  height: 16px;
  color: #4b5563;
  vertical-align: middle;
  margin-left: 4px;
}

.ap-td {
  padding: 8px 10px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
}

.ap-td--agent {
  min-width: 264px;
  padding: 0 8px 0 0;
}

.ap-td--metric {
  min-width: 134px;
}

.ap-sticky {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff !important;
  box-shadow: 8px 0 12px -12px rgba(0, 0, 0, 0.28);
  background-clip: padding-box;
}

.ap-th.ap-sticky {
  z-index: 6;
}

.ap-sticky::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -24px;
  width: 24px;
  background: #ffffff;
  pointer-events: none;
}

.ap-td--agent,
.ap-th--agent {
  background: #ffffff !important;
}

.ap-table-scroll {
  background: #ffffff;
}

.ap-agent {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0;
  background: #ffffff;
}

.ap-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ap-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ap-avatar__ini {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.ap-agent__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.ap-agent__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ap-agent__email {
  font-size: 12px;
  line-height: 1.2;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ap-metric-cell {
  min-height: 56px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 6px;
  border-bottom: 1px solid transparent;
}

.ap-metric-cell--plain {
  background: transparent;
  border-radius: 0;
  border-bottom: 0;
}

.ap-cell-value {
  font-size: 13px;
  line-height: 1.2;
  font-weight: 600;
  color: #111827;
  text-align: center;
  white-space: nowrap;
}

.ap-cell-diff {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.ap-cell-diff--pos {
  color: #157f3f;
}

.ap-cell-diff--neg {
  color: #b42318;
}

.cell-positive-high {
  background-color: color-mix(in srgb, #7edd92 90%, transparent);
}

.cell-positive-medium {
  background-color: color-mix(in srgb, #7edd92 70%, transparent);
}

.cell-positive-low {
  background-color: color-mix(in srgb, #7edd92 30%, transparent);
}

.cell-positive-lowest {
  background-color: #f5f5f5;
}

.cell-negative-high {
  background-color: color-mix(in srgb, #ffb7b7 90%, transparent);
}

.cell-negative-medium {
  background-color: color-mix(in srgb, #ffb7b7 70%, transparent);
}

.cell-negative-low {
  background-color: color-mix(in srgb, #ffb7b7 30%, transparent);
}

.cell-negative-lowest {
  background-color: #f5f5f5;
}

.cell-neutral {
  background-color: #f5f5f5;
}

.ap-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
  background: #ffffff;
}

.ap-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ap-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes ap-spin {
  to {
    transform: rotate(360deg);
  }
}

:global(body.dark) .ap-panel,
:global(body.dark) .ap-hdr,
:global(body.dark) .ap-body,
:global(body.dark) .ap-card,
:global(body.dark) .ap-chart-panel,
:global(body.dark) .ap-tbl,
:global(body.dark) .ap-sticky,
:global(body.dark) .ap-metric-cell--plain {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

:global(body.dark) .ap-hdr-title,
:global(body.dark) .ap-detail-title,
:global(body.dark) .ap-agent__name,
:global(body.dark) .ap-cell-value {
  color: #111827 !important;
}

:global(body.dark) .ap-target,
:global(body.dark) .ap-agent__email {
  color: #4b5563 !important;
}

:global(body.dark) .ap-th,
:global(body.dark) .ap-th *,
:global(body.dark) .ap-card-label {
  color: #111827 !important;
}

:global(body.dark) .ap-target--achieved,
:global(body.dark) .ap-target--achieved .ap-target-copy {
  color: #157f3f !important;
}

::global(body.theme-mix) .ap-panel,
::global(body.theme-mix) .ap-hdr,
::global(body.theme-mix) .ap-body,
::global(body.theme-mix) .ap-card,
::global(body.theme-mix) .ap-chart-panel,
::global(body.theme-mix) .ap-tbl,
::global(body.theme-mix) .ap-sticky,
::global(body.theme-mix) .ap-metric-cell--plain {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

::global(body.theme-mix) .ap-hdr-title,
::global(body.theme-mix) .ap-detail-title,
::global(body.theme-mix) .ap-agent__name,
::global(body.theme-mix) .ap-cell-value {
  color: #111827 !important;
}

::global(body.theme-mix) .ap-target,
::global(body.theme-mix) .ap-agent__email {
  color: #4b5563 !important;
}

::global(body.theme-mix) .ap-th,
::global(body.theme-mix) .ap-th *,
::global(body.theme-mix) .ap-card-label {
  color: #111827 !important;
}

::global(body.theme-mix) .ap-target--achieved,
::global(body.theme-mix) .ap-target--achieved .ap-target-copy {
  color: #157f3f !important;
}

@media (max-width: 1200px) {
  .ap-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .ap-hdr-title {
    font-size: 26px;
  }

  .ap-detail-title {
    font-size: 28px;
  }

  .ap-cards {
    grid-template-columns: 1fr;
  }
}
</style>
